import { create } from "zustand";
import { answerRepository } from "../repository/AnswerRepository";
import { beautyProfilerAllureRepository } from "../repository/BeautyProfilerAllureRepository";
import { beautyProfilerProductRepository } from "../repository/ProductRepository";
import { questionRepository } from "../repository/QuestionRepository";
import { fragranceProfilerRepository } from "../repository/FragranceProfilerRepository";
import { AnswerOptionId } from "../types/AnswerOption";
import { Profiler, QuestionId } from "../types/Question";
import { computeBeautyProfilerResults } from "../scores/beauty-profiler/beautyProfilerResults";
import { BeautyProfilerResults } from "../types/BeautyProfilerResults";
import { FragranceProfilerResults } from "../types/FragranceProfilerResults";
import { computeFragranceProfilerResults } from "../scores/fragrance-profiler/fragranceProfilerResults";

export type ProfilerAnswers = Map<QuestionId, AnswerOptionId>;

interface ProfilerState {
  profiler: Profiler | null;
  questionIds: QuestionId[];
  answers: ProfilerAnswers;
  isReady: boolean;
  currentQuestionId: QuestionId | null;
  totalQuestions: number;
  beautyProfilerResults: BeautyProfilerResults | null;
  fragranceProfilerResults: FragranceProfilerResults | null;
  gift: boolean;

  initialize: (profiler: Profiler) => Promise<void>;
  submitAnswer: (answerId: AnswerOptionId) => void;
  goBack: () => void;
  reset: () => void;

  getNextQuestionId: () => QuestionId | null;
  isComplete: () => boolean;
  getCurrentQuestion: () => any;
  getProgress: () => number;
  getAnswerById: (answerId: AnswerOptionId) => any;
  getAllureById: (allureId: number) => any;
  getProductById: (productId: number) => any;

  computeBeautyProfilerResults: () => void;
  computeFragranceProfilerResults: () => void;
}

export const useProfilerStore = create<ProfilerState>((set, get) => ({
  profiler: null,
  questionIds: [],
  answers: new Map(),
  isReady: false,
  currentQuestionId: null,
  totalQuestions: 0,
  beautyProfilerResults: null,
  fragranceProfilerResults: null,
  gift: false,

  initialize: async (profiler: Profiler) => {
    try {
      set({ profiler });
      await questionRepository.initialize();
      await answerRepository.initialize();
      await beautyProfilerAllureRepository.initialize();
      await beautyProfilerProductRepository.initialize();
      await fragranceProfilerRepository.initialize();
      const questionSet = questionRepository.getQuestionSetByProfiler(profiler);
      const firstQuestionId = Math.min(...questionSet);

      set({
        questionIds: questionSet,
        totalQuestions: questionSet.length,
        currentQuestionId: firstQuestionId,
        isReady: true,
      });
    } catch (error) {
      console.error("Failed to initialize profiler form:", error);
    }
  },

  submitAnswer: (answerId: AnswerOptionId) => {
    const { currentQuestionId, answers } = get();
    if (!currentQuestionId) return;

    const newAnswers = new Map(answers).set(currentQuestionId, answerId);
    set({ answers: newAnswers });
    if (answerId === 42) {
      set({ gift: true });
    }
    if (answerId === 41) {
      set({ gift: false });
    }
    const nextId = get().getNextQuestionId();
    set({ currentQuestionId: nextId });
    console.log("Answer submitted: ", newAnswers);
  },

  goBack: () => {
    const { answers } = get();
    if (answers.size === 0) return;

    const answeredQuestionIds = Array.from(answers.keys());
    const lastAnsweredId = Math.max(...answeredQuestionIds);

    const newAnswers = new Map(answers);
    newAnswers.delete(lastAnsweredId);

    set({
      answers: newAnswers,
      currentQuestionId: lastAnsweredId,
    });
  },

  reset: () => {
    set({
      profiler: null,
      questionIds: [],
      answers: new Map(),
      isReady: false,
      currentQuestionId: null,
      totalQuestions: 0,
      beautyProfilerResults: null,
    });
  },

  getNextQuestionId: () => {
    const { isReady, answers, questionIds } = get();
    if (!isReady) return null;
    const answeredQuestionIds = Array.from(answers.keys());
    const currentQuestionIndex = answeredQuestionIds.length;
    if (currentQuestionIndex >= questionIds.length) {
      return null;
    }
    return questionIds[currentQuestionIndex];
  },

  isComplete: () => {
    const { currentQuestionId, answers, totalQuestions } = get();
    console.log("isComplete", currentQuestionId, answers.size, totalQuestions);
    return (
      currentQuestionId === null &&
      answers.size === totalQuestions &&
      answers.size > 0
    );
  },

  getCurrentQuestion: () => {
    const { currentQuestionId, isReady } = get();
    if (!currentQuestionId || !isReady) return null;
    return questionRepository.getQuestionById(currentQuestionId);
  },

  getProgress: () => {
    const { totalQuestions, answers } = get();
    if (totalQuestions === 0) return 0;
    return Math.ceil(((answers.size + 1) / totalQuestions) * 100);
  },

  getAnswerById: (answerId: AnswerOptionId) =>
    answerRepository.getAnswerById(answerId),

  getAllureById: (allureId: number) =>
    beautyProfilerAllureRepository.getAllureById(allureId),

  getProductById: (productId: number) =>
    beautyProfilerProductRepository.getProductById(productId),

  computeBeautyProfilerResults: () => {
    const { beautyProfilerResults } = get();
    if (beautyProfilerResults !== null) {
      return;
    }

    const { profiler, answers, isReady } = get();
    const results = computeBeautyProfilerResults(profiler, answers, isReady);
    set({ beautyProfilerResults: results });
  },

  computeFragranceProfilerResults: () => {
    const { fragranceProfilerResults } = get();
    if (fragranceProfilerResults !== null) {
      return;
    }

    const { profiler, answers, isReady } = get();
    const results = computeFragranceProfilerResults(profiler, answers, isReady);
    set({ fragranceProfilerResults: results });
  },
}));
